import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./BoundPaywall.module.scss"

// --- The book's pages -------------------------------------------------------
// Page 0 is the hardcover; the rest are photographic spreads whose captions
// each carry one plank of the pitch. Photos are bundled locally so the page
// renders offline and on the live deploy.

type Leaf =
  | { kind: "cover" }
  | {
      kind: "spread"
      img: string
      chapter: string
      title: string
      body: string
      stamp?: string
    }
  | { kind: "colophon" }

const LEAVES: Leaf[] = [
  { kind: "cover" },
  {
    kind: "spread",
    img: "/bound/cover.jpg",
    chapter: "Frontispiece",
    title: "Three hundred\nand sixty-five days.",
    body: "One book. We keep an eye on your whole year and quietly set the best of it aside.",
    stamp: "1,204 photos kept",
  },
  {
    kind: "spread",
    img: "/bound/birthday.jpg",
    chapter: "Chapter One",
    title: "The keepers,\ngathered for you.",
    body: "Birthdays, trips, an ordinary Tuesday that turned out to matter. Auto-curated — you just approve the picks.",
    stamp: "Curated automatically",
  },
  {
    kind: "spread",
    img: "/bound/summit.jpg",
    chapter: "Chapter Two",
    title: "Printed from\nyour originals.",
    body: "Full resolution, exactly as you shot it. Never squeezed, never re-compressed to fit.",
    stamp: "True to the original",
  },
  {
    kind: "spread",
    img: "/bound/beach.jpg",
    chapter: "Chapter Three",
    title: "A hardcover\nevery year.",
    body: "One linen-bound annual, printed and delivered to your door — included with Premium.",
    stamp: "Delivered · included",
  },
  {
    kind: "spread",
    img: "/bound/picnic.jpg",
    chapter: "Chapter Four",
    title: "Yours to keep,\nboth ways.",
    body: "The book sits on your shelf. Every digital copy stays right where it is — always, even if you stop.",
    stamp: "Nothing is ever taken away",
  },
  { kind: "colophon" },
]

const LAST = LEAVES.length - 1

function BoundPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  // page = number of leaves already turned (0 = closed cover on top)
  const [page, setPage] = useState(0)
  const [drag, setDrag] = useState<{ i: number; t: number } | null>(null)
  const bookRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ i: number; startX: number; w: number } | null>(null)

  const go = useCallback((next: number) => {
    setPage((p) => {
      const target = typeof next === "number" ? next : p
      return Math.max(0, Math.min(LAST, target))
    })
  }, [])

  // Auto-flip a couple of pages on load to teach the gesture, then stop.
  useEffect(() => {
    if (reduce) return
    const t1 = setTimeout(() => setPage(1), 900)
    const t2 = setTimeout(() => setPage(2), 1750)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [reduce])

  // --- drag-to-peel ---------------------------------------------------------
  const onDown = (i: number) => (e: React.PointerEvent) => {
    if (i !== page) return
    const w = bookRef.current?.clientWidth ?? 300
    dragRef.current = { i, startX: e.clientX, w }
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onMove = (e: React.PointerEvent) => {
    const d = dragRef.current
    if (!d) return
    const dx = e.clientX - d.startX
    const t = Math.max(0, Math.min(1, -dx / d.w))
    setDrag({ i: d.i, t })
  }
  const onUp = () => {
    const d = dragRef.current
    const cur = drag
    dragRef.current = null
    setDrag(null)
    if (!d) return
    if (cur && cur.t > 0.35) go(d.i + 1)
  }

  const handleClose = () => native.postNativeEvent("close")
  const handleBind = () => {
    if (yearlyProduct) native.subscribe(yearlyProduct)
  }
  const handleMonthly = () => {
    if (monthlyProduct) native.subscribe(monthlyProduct)
  }

  const atOffer = page >= LAST

  return (
    <div className={styles.room}>
      <div className={styles.grain} aria-hidden />

      <button className={styles.close} onClick={handleClose} aria-label="Close">
        ×
      </button>

      {/* masthead / running head */}
      <header className={styles.masthead}>
        <span className={styles.mastLeft}>THE ANNUAL</span>
        <span className={styles.mastMid}>· printed keepsakes ·</span>
        <span className={styles.mastRight}>
          {page === 0 ? "cover" : `p. ${page} / ${LAST}`}
        </span>
      </header>

      {/* the book */}
      <div className={styles.stage}>
        <div className={styles.book} ref={bookRef}>
          <div className={styles.shelf} aria-hidden />
          <div className={styles.spine} aria-hidden />
          <div className={styles.pageBase}>
            {/* the page underneath the top one, so a peel reveals content */}
            {renderLeaf(LEAVES[Math.min(page + 1, LAST)], true, savings)}
          </div>

          {LEAVES.map((leaf, i) => {
            const turned = i < page
            const isTop = i === page
            const dragT = drag && drag.i === i ? drag.t : 0
            const rot = turned ? -180 : isTop ? -dragT * 180 : 0
            const z = turned ? i : LEAVES.length - i
            return (
              <div
                key={i}
                className={`${styles.leaf} ${leaf.kind === "cover" ? styles.coverLeaf : ""}`}
                style={{
                  zIndex: z,
                  transform: `rotateY(${rot}deg)`,
                  transition: drag ? "none" : undefined,
                  pointerEvents: isTop ? "auto" : "none",
                }}
                onPointerDown={onDown(i)}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                onClick={() => isTop && !drag && i < LAST && go(i + 1)}
              >
                <div className={styles.front}>
                  {renderLeaf(leaf, false, savings)}
                </div>
                <div className={styles.back} aria-hidden>
                  <div className={styles.backPaper} />
                </div>
              </div>
            )
          })}

          {/* corner curl hint on the top page */}
          {page > 0 && page < LAST && !drag && !reduce && (
            <motion.div
              className={styles.curl}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 1, 0], x: [0, -8, -8, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.2 }}
              aria-hidden
            />
          )}
        </div>

        {/* bookmark ribbon page dots */}
        <div className={styles.ribbon} aria-hidden>
          {LEAVES.map((_, i) => (
            <span
              key={i}
              className={`${styles.tick} ${i <= page ? styles.tickOn : ""}`}
            />
          ))}
        </div>
      </div>

      {/* the bound-in subscription reply card = the offer */}
      <motion.section
        className={`${styles.card} ${atOffer ? styles.cardLift : ""}`}
        initial={false}
        animate={reduce ? {} : { y: atOffer ? -6 : 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 22 }}
      >
        <div className={styles.perf} aria-hidden />
        <div className={styles.cardHead}>
          <span className={styles.cardKicker}>Subscription card · tip-in</span>
          <span className={styles.cardTear}>tear along dotted line</span>
        </div>

        <div className={styles.priceRow}>
          <div className={styles.priceMain}>
            <span className={styles.priceBig}>{yearlyPrice}</span>
            <span className={styles.priceUnit}>
              per year
              <em>{yearlyPerMonth}/mo, billed annually</em>
            </span>
          </div>
          {savings > 0 && (
            <div className={styles.savings}>
              save {savings}%<span>vs monthly</span>
            </div>
          )}
        </div>

        <button className={styles.cta} onClick={handleBind}>
          <span>Bind my book</span>
          <span className={styles.ctaSub}>
            start Premium · 7-day free trial
          </span>
        </button>

        <button className={styles.monthly} onClick={handleMonthly}>
          or subscribe monthly at {monthlyPrice}/mo
        </button>

        <p className={styles.fine}>
          7-day free trial, then {yearlyPrice}/year. Auto-renews until
          cancelled. Cancel anytime. Your photos stay yours whether you
          subscribe or not.
        </p>

        <div className={styles.colophon}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </motion.section>
    </div>
  )
}

function renderLeaf(leaf: Leaf, under: boolean, savings: number) {
  if (leaf.kind === "cover") {
    return (
      <div className={styles.cover}>
        <div className={styles.coverInner}>
          <span className={styles.coverKicker}>Vol. I · MMXXVI</span>
          <h1 className={styles.coverTitle}>
            YOUR
            <br />
            YEAR,
            <br />
            <span>BOUND</span>
          </h1>
          <span className={styles.coverRule} />
          <span className={styles.coverSub}>a hardcover printed for you</span>
          {!under && <span className={styles.openHint}>tap to open ▸</span>}
        </div>
        <div className={styles.coverCloth} aria-hidden />
      </div>
    )
  }

  if (leaf.kind === "colophon") {
    return (
      <div className={styles.colo}>
        <span className={styles.coloMark}>❦</span>
        <h2>Colophon</h2>
        <p>
          Set in Red Hat Display &amp; Krona One. Curated by the app from the
          year you lived. Printed on demand, bound in linen, and posted to your
          door.
        </p>
        <p className={styles.coloKeep}>
          The digital copies remain yours, always.
        </p>
        <span className={styles.coloEnd}>— turn to the card —</span>
      </div>
    )
  }

  return (
    <div className={styles.spread}>
      <div
        className={styles.photo}
        style={{ backgroundImage: `url(${leaf.img})` }}
      >
        {leaf.stamp && !under && (
          <span className={styles.stamp}>{leaf.stamp}</span>
        )}
      </div>
      <div className={styles.caption}>
        <span className={styles.chapter}>{leaf.chapter}</span>
        <h3>
          {leaf.title.split("\n").map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </h3>
        <p>{leaf.body}</p>
      </div>
    </div>
  )
}

export default BoundPaywall
